// useRenewNow (Story 10.5, AC2) — the touchpoint-aware renewal CTA.
//
// The fork lives SERVER-side (Decision 2): the endpoint mints a Stripe Customer
// Portal session when the user has a `stripe_customer_id`, and returns the
// configured `RENEWAL_CTA_URL` otherwise — the FE never branches on user origin.
//
// Popup-blocker gotcha: `window.open` called after an awaited fetch falls outside
// the user-gesture window and gets blocked in Safari/Chrome, so the tab is opened
// synchronously in the click handler and navigated when the POST resolves. The
// `"noopener"` FEATURE cannot be used here — passing it makes `window.open`
// return null by spec, which would defeat the deferred navigation — so the opener
// link is severed by hand instead (same isolation). If the popup is blocked
// (`window.open` → null), fall back to same-tab navigation: acceptable, since the
// 30s re-check restores state when the user navigates back (Dev Notes).

import { useMutation } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";

/** Server fork: Customer Portal session or configured renewal link (Task 5 slice). */
export const RENEWAL_CTA_PATH = "/billing/renewal-cta-sessions/create";

interface RenewalCtaResponse {
  url: string;
}

interface RenewNowVariables {
  /** Placeholder tab opened synchronously inside the click gesture (null = blocked). */
  tab: Window | null;
}

/**
 * Defense-in-depth: the server only ever returns a Stripe portal URL or the
 * configured RENEWAL_CTA_URL, but the placeholder tab is app-origin — a future
 * server-side regression echoing request input must never become a
 * `javascript:`-URI sink here. Anything but http(s) is treated as an error.
 */
function assertHttpUrl(url: string): void {
  const protocol = new URL(url, window.location.origin).protocol;
  if (protocol !== "https:" && protocol !== "http:") {
    throw new Error("Refused a non-http(s) renewal URL");
  }
}

export interface RenewNow {
  /** Open the renewal touchpoint in a new tab (call from a click handler only). */
  renewNow: () => void;
  isPending: boolean;
  isError: boolean;
}

export function useRenewNow(): RenewNow {
  // Navigation/cleanup live in the HOOK-level callbacks (tab handle rides the
  // mutation variables): callbacks passed to `mutate()` are silently dropped if
  // the observer unmounts before the POST settles — reachable here (modal →
  // "View records" swaps the observer mid-flight) and it would strand the
  // placeholder tab on about:blank.
  const mutation = useMutation({
    mutationFn: async (_variables: RenewNowVariables) => {
      const data = await apiFetch<RenewalCtaResponse>(RENEWAL_CTA_PATH, {
        method: "POST",
      });
      assertHttpUrl(data.url);
      return data;
    },
    onSuccess: ({ url }, { tab }) => {
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        // Popup blocked, or the user closed the placeholder mid-flight —
        // same-tab navigation completes the intent (Dev Notes degradation).
        window.location.assign(url);
      }
    },
    onError: (_error, { tab }) => {
      if (tab && !tab.closed) tab.close();
    },
  });

  const renewNow = () => {
    if (mutation.isPending) return;
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    mutation.mutate({ tab });
  };

  return { renewNow, isPending: mutation.isPending, isError: mutation.isError };
}
